import type { Fetcher } from "../fetcher";
import type { ApiSingleResponse, ApiCollectionResponse } from "../types/api";
import type { Quote, QuoteStat } from "../entities/quote";
import type { QuoteCargo } from "../entities/cargo";
import type {
  QuoteFilters,
  QuotesListData,
  CreateQuoteDto,
  UpdateQuoteDto,
  PatchRouteDto,
  CancelQuoteDto,
  ChangeQuoteStatusDto,
  ConfirmAndCreateOrderDto,
  CreateQuoteCargoDto,
  UpdateQuoteCargoDto,
  DeleteQuoteCargoResponse,
  QuoteEventDTO,
  ListQuoteEventsParams,
  QuoteCommentDTO,
  ListQuoteCommentsParams,
  CreateQuoteCommentDto,
  UpdateQuoteCommentDto,
  QuoteImportResult,
  OrderPreview,
} from "./types";

/**
 * Repository for quote-related endpoints.
 *
 * Backend wraps responses in `{ success, data, message }` via `sendSuccess`.
 * The fetcher returns the parsed JSON and we read `response.data` directly.
 * Localisation is server-side via the `X-Language` header (set by the
 * fetcher) — repository doesn't need a `locale` argument.
 */
export function createQuotesRepository(fetcher: Fetcher) {
  return {
    getQuote: async (id: string): Promise<Quote> => {
      const res = await fetcher<ApiSingleResponse<Quote>>(`/quotes/${id}`);
      return res.data;
    },

    getQuotes: async (params: QuoteFilters | Record<string, unknown> = {}): Promise<QuotesListData> => {
      const res = await fetcher<
        ApiCollectionResponse<Quote, { stats?: QuoteStat[]; totalPages?: number }>
      >("/quotes", { params: params as Record<string, unknown> });
      const payload = res.data ?? ({} as Partial<QuotesListData>);
      const pagination = (payload.pagination ?? {}) as Partial<QuotesListData["pagination"]>;
      return {
        items: payload.items ?? [],
        stats: payload.stats,
        pagination: {
          page: pagination.page ?? 1,
          limit: pagination.limit ?? 20,
          total: pagination.total ?? 0,
          totalPages: pagination.totalPages ?? 0,
        },
      };
    },

    // Count-only — for the filter workspace's live result count.
    getQuotesCount: async (
      params: QuoteFilters | Record<string, unknown> = {},
    ): Promise<number> => {
      const res = await fetcher<ApiSingleResponse<{ count: number }>>(
        "/quotes/count",
        { params: params as Record<string, unknown> },
      );
      return res.data?.count ?? 0;
    },

    deleteQuote: async (id: string): Promise<void> => {
      await fetcher(`/quotes/${id}`, { method: "DELETE" });
    },

    createQuote: async (data: CreateQuoteDto): Promise<Quote> => {
      const res = await fetcher<ApiSingleResponse<Quote>>("/quotes", {
        method: "POST",
        body: JSON.stringify(data),
      });
      return res.data;
    },

    /**
     * The backend's PATCH /quotes/:id may respond with `{ success, message }`
     * and no body for some flows; the caller invalidates the cache and
     * refetches. Returning `null` on the empty-data path lets that pattern
     * keep working without crashing on `res.data.client`.
     */
    updateQuote: async (id: string, data: UpdateQuoteDto): Promise<Quote | null> => {
      const res = await fetcher<ApiSingleResponse<Quote>>(`/quotes/${id}`, {
        method: "PATCH",
        body: JSON.stringify(data),
      });
      return res.data ?? null;
    },

    updateQuoteRoute: async (
      id: string,
      data: PatchRouteDto,
      /** Pass 'wizard' on step 2 of the Create Quote wizard — the
       *  backend reads `?source=wizard` to also advance status from
       *  draft to waiting_for_offer. */
      source?: "wizard",
    ): Promise<Quote | null> => {
      const qs = source ? `?source=${source}` : "";
      const res = await fetcher<ApiSingleResponse<Quote>>(`/quotes/${id}/route${qs}`, {
        method: "PATCH",
        body: JSON.stringify(data),
      });
      return res.data ?? null;
    },

    archiveQuote: async (id: string, data: { reason: string }): Promise<{ message: string }> => {
      const res = await fetcher<{ message: string }>(`/quotes/${id}/archive`, {
        method: "POST",
        body: JSON.stringify(data),
      });
      return res;
    },

    cancelQuote: async (id: string, data: CancelQuoteDto): Promise<Quote | null> => {
      const res = await fetcher<ApiSingleResponse<Quote>>(`/quotes/${id}/cancel`, {
        method: "POST",
        body: JSON.stringify(data),
      });
      return res.data ?? null;
    },

    /**
     * Read-only preview of the order this quote would create, for the
     * "Approve" window. `scenarioId` selects the pricing scenario;
     * omitting it falls back to confirmed → primary server-side.
     */
    getOrderPreview: async (
      id: string,
      scenarioId?: string,
    ): Promise<OrderPreview | null> => {
      const res = await fetcher<ApiSingleResponse<OrderPreview>>(
        `/quotes/${id}/order-preview`,
        { params: scenarioId ? { scenarioId } : undefined },
      );
      return res.data ?? null;
    },

    changeQuoteStatus: async (id: string, data: ChangeQuoteStatusDto): Promise<Quote | null> => {
      const res = await fetcher<ApiSingleResponse<Quote>>(`/quotes/${id}/status`, {
        method: "PATCH",
        body: JSON.stringify(data),
      });
      return res.data ?? null;
    },

    confirmAndCreateOrder: async (
      id: string,
      data: ConfirmAndCreateOrderDto,
    ): Promise<{
      quoteId: string;
      calcSetId: string;
      orderCreated?: boolean;
      orderId?: string | null;
      status?: string;
    }> => {
      const res = await fetcher<ApiSingleResponse<{
        quoteId: string;
        calcSetId: string;
        orderCreated?: boolean;
        orderId?: string | null;
        status?: string;
      }>>(`/quotes/${id}/confirm-and-create-order`, {
        method: "POST",
        body: JSON.stringify(data),
      });
      return res.data;
    },

    // ─── Activity / Audit Events ──────────────────────────────────────────

    listEvents: async (
      quoteId: string,
      params: ListQuoteEventsParams = {},
    ): Promise<{
      items: QuoteEventDTO[];
      pagination: { page: number; limit: number; total: number; totalPages: number };
    }> => {
      const res = await fetcher<ApiCollectionResponse<QuoteEventDTO>>(
        `/quotes/${quoteId}/events`,
        { params: params as Record<string, unknown> },
      );
      const payload = res.data ?? ({} as Partial<{ items: QuoteEventDTO[]; pagination: { page: number; limit: number; total: number; totalPages: number } }>);
      const pagination = payload.pagination ?? {};
      return {
        items: payload.items ?? [],
        pagination: {
          page: pagination.page ?? 1,
          limit: pagination.limit ?? 20,
          total: pagination.total ?? 0,
          totalPages: pagination.totalPages ?? 1,
        },
      };
    },

    // ─── Comments ─────────────────────────────────────────────────────────

    listComments: async (
      quoteId: string,
      params: ListQuoteCommentsParams = {},
    ): Promise<{
      items: QuoteCommentDTO[];
      pagination: { page: number; limit: number; total: number; totalPages: number };
    }> => {
      const res = await fetcher<ApiCollectionResponse<QuoteCommentDTO>>(
        `/quotes/${quoteId}/comments`,
        { params: params as Record<string, unknown> },
      );
      const payload = res.data ?? ({} as Partial<{ items: QuoteCommentDTO[]; pagination: { page: number; limit: number; total: number; totalPages: number } }>);
      const pagination = payload.pagination ?? {};
      return {
        items: payload.items ?? [],
        pagination: {
          page: pagination.page ?? 1,
          limit: pagination.limit ?? 20,
          total: pagination.total ?? 0,
          totalPages: pagination.totalPages ?? 1,
        },
      };
    },

    createComment: async (
      quoteId: string,
      data: CreateQuoteCommentDto,
    ): Promise<QuoteCommentDTO> => {
      const res = await fetcher<ApiSingleResponse<QuoteCommentDTO>>(
        `/quotes/${quoteId}/comments`,
        { method: "POST", body: JSON.stringify(data) },
      );
      return res.data;
    },

    updateComment: async (
      quoteId: string,
      commentId: string,
      data: UpdateQuoteCommentDto,
    ): Promise<QuoteCommentDTO> => {
      const res = await fetcher<ApiSingleResponse<QuoteCommentDTO>>(
        `/quotes/${quoteId}/comments/${commentId}`,
        { method: "PATCH", body: JSON.stringify(data) },
      );
      return res.data;
    },

    deleteComment: async (quoteId: string, commentId: string): Promise<void> => {
      await fetcher(`/quotes/${quoteId}/comments/${commentId}`, { method: "DELETE" });
    },

    // ─── Cargo Operations ─────────────────────────────────────────────────

    listCargos: async (quoteId: string): Promise<QuoteCargo[]> => {
      const res = await fetcher<ApiCollectionResponse<QuoteCargo>>(
        `/quotes/${quoteId}/cargos`,
      );
      return res.data?.items ?? [];
    },

    getCargo: async (quoteId: string, cargoId: string): Promise<QuoteCargo> => {
      const res = await fetcher<ApiSingleResponse<QuoteCargo>>(
        `/quotes/${quoteId}/cargos/${cargoId}`,
      );
      return res.data;
    },

    createCargo: async (
      quoteId: string,
      data: CreateQuoteCargoDto,
    ): Promise<QuoteCargo> => {
      const res = await fetcher<ApiSingleResponse<QuoteCargo>>(
        `/quotes/${quoteId}/cargos`,
        { method: "POST", body: JSON.stringify(data) },
      );
      return res.data;
    },

    updateCargo: async (
      quoteId: string,
      cargoId: string,
      data: UpdateQuoteCargoDto,
    ): Promise<{ message: string } | unknown> => {
      return fetcher(`/quotes/${quoteId}/cargos/${cargoId}`, {
        method: "PATCH",
        body: JSON.stringify(data),
      });
    },

    deleteCargo: async (
      quoteId: string,
      cargoId: string,
    ): Promise<DeleteQuoteCargoResponse> => {
      const res = await fetcher<ApiSingleResponse<DeleteQuoteCargoResponse>>(
        `/quotes/${quoteId}/cargos/${cargoId}`,
        { method: "DELETE" },
      );
      return res.data ?? { deletedCalculations: [] };
    },

    importFromExcel: async (file: File | Blob): Promise<QuoteImportResult> => {
      const form = new FormData();
      form.append("file", file);
      const res = await fetcher<ApiSingleResponse<QuoteImportResult>>(
        "/quotes/import",
        { method: "POST", body: form as unknown as BodyInit },
      );
      return res.data;
    },
  };
}
